<template>
	<div id="blog" class="blog-wrapper">
		<div class="container">
			<div class="row title-row align-items-center">
				<div class="col-lg-6 col-md-12 my-auto">
					<p class="title">{{ $t('blog.title') }}</p>
				</div>
				<div class="col-lg-6 d-none d-lg-block text-right">
					<a :href="blogUrl" target="_blank" class="more-posts">{{ $t('blog.allPosts') }}</a>
				</div>
			</div>
		</div>
		<div class="container blogs-container">
			<div class="row blogs-row">
				<template v-if="loading">
					<div v-for="n in 3" :key="n" class="skeleton-card">
						<div class="skeleton-image shimmer"></div>
						<div class="skeleton-content">
							<div class="skeleton-line shimmer short"></div>
							<div class="skeleton-line shimmer title"></div>
							<div class="skeleton-line shimmer title medium"></div>
							<div class="skeleton-line shimmer description"></div>
							<div class="skeleton-line shimmer description"></div>
							<div class="skeleton-line shimmer description medium"></div>
							<div class="skeleton-line shimmer read-more"></div>
						</div>
					</div>
				</template>
				<template v-else>
					<Card v-for="post in posts" :key="post.id" :date="formatDate(post.published_at)" :title="post.title"
						:description="post.custom_excerpt || post.excerpt" :image="post.feature_image"
						:linkHU="post.url" :linkEN="post.url" />
				</template>
			</div>
		</div>
		<div class="container">
			<div class="row button-row">
				<div class="col-md-12 d-lg-none d-xl-none margin-m">
					<BaseButton :text="$t('blog.allPosts')" size="large" type="primary"
						@clicked="handleButtonClick()" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment';

export default {
	name: 'Blog',
	props: {},
	components: {},
	data: () => ({
		posts: [],
		loading: true,
	}),
	created() {
		this.fetchPosts();
	},

	computed: {
		blogUrl() {
			return this.$i18n.locale === 'hu'
				? 'https://blog.blockben.com/hu/'
				: 'https://blog.blockben.com';
		},
	},
	watch: {
		'$i18n.locale'() {
			this.fetchPosts();
		},
	},
	methods: {
		async fetchPosts() {
			try {
				const key = process.env.VUE_APP_GHOST_API_KEY;
				const locale = this.$i18n.locale;
				const filter = locale === 'hu' ? 'filter=tag:hu' : 'filter=tag:-hu';
				const limit = 3;
				const fields = 'id,title,published_at,custom_excerpt,excerpt,feature_image,url';
				const response = await fetch(
					`https://blog.blockben.com/ghost/api/content/posts/?limit=${limit}&key=${key}&${filter}&fields=${fields}`
				);
				const data = await response.json();
				this.posts = data.posts || [];
			} catch (e) {
				console.error('Failed to fetch blog posts', e);
			} finally {
				this.loading = false;
			}
		},
		formatDate(dateStr) {
			return moment(dateStr).locale(this.$i18n.locale).format('D. MMM YYYY');
		},
		handleButtonClick() {
			window.open(this.blogUrl, '_blank');
		},
	},
};
</script>

<style lang="scss" scoped>
.blog-wrapper {
	padding: 60px 0 75px;
}

.title {
	font-family: 'HKNova-Bold';
	font-size: $headline-l;
	color: $black;
	margin: 0;
	text-transform: uppercase;
}

.text {
	font-size: $text-l;
	color: $black;
}

.bb-arrow-down {
	display: inline-block;
}

.title-row {
	margin-bottom: 60px;
}

.right-arrow {
	display: inline-block;
	transform: rotate(-90deg);
	font-size: $text-l;
}

.more-posts {
	font-family: 'HKNova', sans-serif;
	font-weight: 400;
	color: #4b2eaa !important;

	&:hover {
		color: $link-hover-color !important;

		&:after {
			background: $link-hover-color;
		}
	}

	&:after {
		content: '';
		width: 10px;
		height: 17px;
		background: #4b2eaa;
		display: inline-block;
		mask-image: url('/images/icons/bb-icon_arrow-right.svg');
		margin: 0 0 0 10px;
		top: 2px;
		position: relative;
		transition: $transition-global;
	}
}

.margin-m {
	margin-top: $margin-m;
}

.margin-l-bottom {
	margin-bottom: $margin-l;
}

.card-col {
	margin: auto;
	overflow-x: auto;
	white-space: nowrap;
	height: 610px;
}

.card:not(:last-child) {
	margin-right: 15px;
}

.blogs-container {
	@media (max-width: $breakpoint-md) {
		width: 100%;
		max-width: 100%;
		padding: 0;
	}
}

.skeleton-card {
	height: 600px;
	width: 100%;
	background: $white;
	box-shadow: 0px 4px 8px rgba(75, 46, 170, 0.1);
	overflow: hidden;
	position: relative;
}

.skeleton-image {
	width: 100%;
	height: 250px;
}

.skeleton-content {
	padding: 20px;
}

.skeleton-line {
	height: 14px;
	border-radius: 4px;
	margin-bottom: 12px;
	width: 100%;

	&.short {
		width: 40%;
		margin-bottom: 0;
	}

	&.medium {
		width: 60%;
	}

	&.title {
		height: 20px;
		margin-top: 15px;
		margin-bottom: 0;

		&+.title {
			margin-top: 4px;
		}
	}

	&.description {
		height: 13px;
		margin-top: 15px;
		margin-bottom: 0;

		&+.description {
			margin-top: 4px;
		}
	}

	&.read-more {
		height: 16px;
		width: 30%;
		position: absolute;
		bottom: 40px;
		left: 20px;
		margin: 0;
	}
}

@keyframes shimmer {
	0% {
		background-position: -600px 0;
	}

	100% {
		background-position: 600px 0;
	}
}

.shimmer {
	background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
	background-size: 1200px 100%;
	animation: shimmer 1.4s infinite linear;
}

.blogs-row {
	display: grid;
	grid-template-columns: repeat(3, 350px);
	column-gap: 30px;
	justify-content: center;

	@media (max-width: $breakpoint-lg) {
		grid-template-columns: repeat(3, 300px);
		justify-content: flex-start;
	}

	@media (max-width: $breakpoint-md) {
		overflow-x: scroll;
		padding: 15px 25px;
		column-gap: 20px;
		margin: 0;
	}
}
</style>
